import { Icon, HStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Control } from 'react-hook-form';

import { InputMoneyProps } from '../../../components/InputMoney';
import { TableContentItemColumn, TableContentItemText } from '../../../components/TableContent';
import { messageError } from '../../Resume/utils';
import { AddedProductItemCoesProps } from '../components/Cart/components/AddedProductItem/Coes';
import { useCoesAssetController } from '../components/DynamicAsset/hooks/products/Coes';
import { getCoeVariationStyle } from '../utils';
import { CoeVariationStyleValueProp } from '../utils/types';

import { useBatchTicketCartActionItem } from './batchTicketCartActionItem';

interface useCoeAddedProductItemProps extends AddedProductItemCoesProps {}

export const useCoeAddedProductItem = ({ data: item, type }: useCoeAddedProductItemProps) => {
  const inputIsDisabled = type === 'requiredAttention';

  const { isLoadingAction, onRemoveProduct, onUpdateProduct } =
    useBatchTicketCartActionItem<'coes'>({
      item,
      id: 'coes',
      idSelector: 'productId',
      type,
    });

  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    isFocused,
    helpText,
    currentValueFormatted,
  } = useCoesAssetController(item, onUpdateProduct);

  const isRequiredAttention = type === 'requiredAttention';
  const isTotalSummary = type === 'totalSummary';
  const useAmountValue = (isRequiredAttention || isTotalSummary) && item.amount;
  const currentValue = useAmountValue ? Utils.Mask.Money(item.amount) : currentValueFormatted;

  const inputMoneyDataProps = {
    id: `product-added-applied-amount-${item.offerId}`,
    name: 'createAppliedAmount',
    control: control as unknown as Control,
    isDisabled: inputIsDisabled,
    currentValue,
    hasFocus: isFocused,
    minValue: item.minimumApplication,
    customInputProps: {
      ...(isFocused && hasError && { error: hasError }),
      ...(isFocused && !hasError && { helpText }),
    },
    handleFocus: handleInputFocus,
    handleBlur: handleInputBlur,
    handleSubmit,
    handleClear: handleInputClear,
  } as InputMoneyProps;

  const additionalInfoComponent = item.additionalInfo?.slice(0, 2).map((additionalInfo) => {
    const additionalInfoVariationStyle = getCoeVariationStyle(
      (additionalInfo?.displayTypeId as CoeVariationStyleValueProp) || 'error'
    );

    return (
      <TableContentItemColumn
        justifyContent={{ base: 'space-between', sm: 'flex-start' }}
        direction={{ base: 'row', sm: 'column' }}
        key={additionalInfo.sort.toString()}
        space={Spacing['inline.quarck']}>
        <TableContentItemText>{additionalInfo.description || messageError}</TableContentItemText>
        <HStack space={Spacing['inline.quarck']} alignItems={'center'}>
          {additionalInfoVariationStyle &&
            additionalInfoVariationStyle?.name &&
            additionalInfoVariationStyle?.color && (
              <Icon
                name={additionalInfoVariationStyle?.name}
                color={Color[additionalInfoVariationStyle?.color]}
                size="xxxxs"
                testID="icon-arrow"
              />
            )}
          <TableContentItemText>
            {Utils.Mask.Percentage(additionalInfo?.value || 0)}
          </TableContentItemText>
        </HStack>
      </TableContentItemColumn>
    );
  });

  return {
    additionalInfoComponent,
    inputMoneyDataProps,
    onRemoveProduct,
    isLoadingAction,
    inputIsDisabled,
  };
};
